/* eslint-disable max-len */
import React, { useState } from 'react';
import styled from 'styled-components';
import { Observer, useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { Icon } from '../../concepts/resources/Icon';
import { ReadStore } from '../../concepts/revive/ReadStore';
import { Table } from '../../ui/Table';
import { Emoji } from '../../ui/emoji';
import { SortMode, applyFilter, applySort } from '../common/Sorter';
import { BoardModel, FolderModel } from '../dashboard/Models';
import { BoardActionMenu } from '../dashboard/elements/BoardActionMenu';

const CellPin = styled.span`
    display: inline-block;

    .emoji {
        margin-right: .25em;
        transform: translate(-.3rem, .2rem) scaleX(-1) scale(1.2);
    }
`;

const formatDate = (date: Date) => date && date instanceof Date && `${date.toLocaleDateString()} @ ${date.toLocaleTimeString()}`;

type CommonProps = { actions?: boolean | Record<string, boolean>, sort: SortMode, filter?: string };
type HasData = { readonly folders?: readonly FolderModel[], readonly boards?: readonly BoardModel[] };
type HasStore = { store?: ReadStore<HasData> };

export type StaticFolderBoardTableProps = CommonProps & HasData;
export type ObservingFolderBoardTableProps = CommonProps & HasStore;

const StaticFolderBoardTable = React.memo<StaticFolderBoardTableProps>(({ actions, folders, boards, sort, filter }) => {

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [tick, setTick] = useState(0);
    folders = applySort(applyFilter(folders ?? [], filter), sort);
    boards = applySort(applyFilter(boards ?? [], filter), sort, ['pinned', 'asc']);

    return <Table>
        <Table.Head>
            <Table.Row>
                <Table.Cell width="1rem"></Table.Cell>
                <Table.Cell>Name</Table.Cell>
                <Table.Cell>Last updated</Table.Cell>
                <Table.Cell>Created</Table.Cell>
                <Table.Cell width="0.1"></Table.Cell>
            </Table.Row>
        </Table.Head>
        <tbody>
            {folders.map(f => <Observer key={f.id} store={f} render={fs =>
                <Table.Row clickable>
                    <Table.LinkCell to={fs.path}>
                        <Icon size={2} src={!!f.icon ? `${f.icon}@[0.9,0.9,0]` : 'deqo://icons/dash/folder-list-icon'} />
                    </Table.LinkCell>
                    <Table.LinkCell to={fs.path}>{fs.label}</Table.LinkCell>
                    <Table.LinkCell to={fs.path}>{formatDate(fs.updatedAt)}</Table.LinkCell>
                    <Table.LinkCell to={fs.path}>{formatDate(fs.createdAt)}</Table.LinkCell>
                    <Table.Cell></Table.Cell>
                </Table.Row>
            } />)}
            {boards.map(b => <Observer key={b.id} store={b} render={bs =>
                <Table.Row clickable>
                    <Table.LinkCell to={bs.path}>
                        <Icon size="2" src="deqo://icons/dash/board-list-icon" />
                    </Table.LinkCell>
                    <Table.LinkCell to={bs.path}>
                        {bs.pinned && <CellPin><Emoji idOrCode=":pushpin:" /></CellPin>}
                        {bs.label}
                    </Table.LinkCell>
                    <Table.LinkCell to={bs.path}>{formatDate(bs.updatedAt)}</Table.LinkCell>
                    <Table.LinkCell to={bs.path}>{formatDate(bs.createdAt)}</Table.LinkCell>
                    <Table.Cell>
                        {!bs.special && <BoardActionMenu config={actions} board={bs} onLayoutAffected={() => setTick(x => x + 1)} />}
                    </Table.Cell>
                </Table.Row>
            } />)}
        </tbody>
    </Table>;
});

const ObservingFolderBoardTable: LFC<ObservingFolderBoardTableProps> = ({ store, ...common }) => {
    const { folders, boards } = useObserver(store);
    return <StaticFolderBoardTable folders={folders} boards={boards} {...common} />;
};

export const FolderBoardTable: LFC<StaticFolderBoardTableProps & ObservingFolderBoardTableProps> =
    ({ folders, boards, store, ...common }) => {
        if (boards && store) throw new Error('FolderBoardTable can only accent data or store prop!');
        return !!boards
            ? <StaticFolderBoardTable folders={folders} boards={boards} {...common} />
            : <ObservingFolderBoardTable store={store} {...common} />;
    };
